import { useMemo, useState } from "react";
import { ZIndex } from "@/domains/design/constants";
import { ScrollDirection, ScrollState } from "@/domains/react/useHorizontalScroll";
import { css, cx } from "@/domains/emotion";
import { MdsIconKind } from "@/design-system/components/icon";
import { MDSIconButton, MDSIconButtonVariant } from "@/mds";
import styled from "@emotion/styled";

interface ScrollButtonProps {
  direction: ScrollDirection;
  scrollState: ScrollState;
  startScrolling: (direction: ScrollDirection) => void;
  stopScrolling: () => void;
  scrollForDuration: (direction: ScrollDirection, duration: number) => void;
  variant: "standard" | "inverted";
  scrollButtonClassName?: string;
}

export const ScrollButton = ({
  direction,
  scrollState,
  startScrolling,
  stopScrolling,
  scrollForDuration,
  scrollButtonClassName,
}: ScrollButtonProps) => {
  const [pressStartTime, setPressStartTime] = useState<number | null>(null);

  const handleMouseDown = () => {
    setPressStartTime(Date.now());
    startScrolling(direction);
  };

  const handleMouseUp = () => {
    if (pressStartTime && Date.now() - pressStartTime < 100) {
      scrollForDuration(direction, 600);
    } else {
      setPressStartTime(null);
      stopScrolling();
    }
  };

  const { isVisible, buttonPositionStyles, iconKind } = useMemo(() => {
    if (direction === ScrollDirection.Left)
      return {
        isVisible: scrollState.leftVisible,
        buttonPositionStyles: buttonLeftStyles,
        iconKind: MdsIconKind.AngleLeft,
      };

    return {
      isVisible: scrollState.rightVisible,
      buttonPositionStyles: buttonRightStyles,
      iconKind: MdsIconKind.AngleRight,
    };
  }, [direction, scrollState]);

  const combinedStyles = cx(buttonPositionStyles, scrollButtonClassName);

  return (
    <StyledIconButton
      variant={MDSIconButtonVariant.Floating}
      iconKind={iconKind}
      onPressStart={handleMouseDown}
      onPressEnd={handleMouseUp}
      $isVisible={isVisible}
      className={combinedStyles}
      rounded
    />
  );
};

const StyledIconButton = styled(MDSIconButton)<{ $isVisible: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: ${ZIndex.FloatingButton};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  cursor: ${({ $isVisible }) => ($isVisible ? "pointer" : "default")};
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
`;

const buttonLeftStyles = css({ left: "3px" });
const buttonRightStyles = css({ right: "3px" });
